import { Button, ConnectionBanner, Spinner } from "@omony/ui";
import { MonitorSmartphone } from "lucide-react";
import { useIntl } from "react-intl";
import { AuthGate } from "../shared/auth/AuthGate.js";
import { navigate, useHashRoute } from "../shared/hashRoute.js";
import { useSession } from "../shared/query.js";
import { useRealtime } from "../shared/realtime.js";
import styles from "./console.module.css";
import { Console } from "./Console.js";
import { KioskApp } from "./device/KioskApp.js";
import { PairScreen } from "./device/PairScreen.js";
import { markJoined } from "./flow/actions.js";
import { useKeepFocusVisible, useUnauthenticatedRefresh } from "./hooks.js";
import { VideoProvider } from "../shared/video.js";

/**
 * Einstieg der Apotheken-Konsole:
 *  - gekoppeltes Kiosk-Gerät → Gerätezustände (gesperrt / Personal / Patientenmodus)
 *  - sonst Apotheken-Konto (Büro-PC) über die normale Anmeldung, oder Kopplung als Kiosk
 */
export function App() {
  const intl = useIntl();
  const session = useSession();
  const [route] = useHashRoute();
  useUnauthenticatedRefresh();
  useKeepFocusVisible();

  if (session.isLoading || !session.data)
    return (
      <div className={styles.center}>
        <Spinner label={intl.formatMessage({ id: "common.loading" })} size={32} />
      </div>
    );
  const dev = session.data.device;
  if (dev?.kind === "signage") {
    location.replace("/signage/");
    return null;
  }
  if (dev?.kind === "kiosk") return <KioskApp session={session.data.session} />;
  if (route[0] === "koppeln") return <PairScreen />;
  return (
    <AuthGate
      appRole="apotheke"
      appTitleId="app.apotheke"
      loginExtra={
        <div className={styles.pairLink}>
          <Button variant="quiet" icon={<MonitorSmartphone />} onPress={() => navigate("koppeln")}>
            {intl.formatMessage({ id: "console.pair.link" })}
          </Button>
        </div>
      }
    >
      {(s) => <AccountConsole name={s.displayName} />}
    </AuthGate>
  );
}

/** Büro-PC mit Apotheken-Konto: gleiche Konsole, ohne Kiosk-Sperre und Patientenmodus */
function AccountConsole({ name }: { name: string }) {
  const connection = useRealtime(true);
  return (
    <VideoProvider onJoined={markJoined}>
      <ConnectionBanner state={connection} />
      <Console mode="account" userName={name} connection={connection} />
    </VideoProvider>
  );
}
